import React, { useEffect, useContext, useCallback, useMemo } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import { FieldValues } from '@sravni/react-silent-authentication-form';
import { pick } from 'lodash';
import { CARD_PURPOSES } from '../../../../../landing.content/components/top.form';
import { useFormData, useFormStore } from '../../../../../../../../store/selectors/formStore';
import { CardsContext } from '../../../../../../context';
import { FORM_FIELDS, PAGE_CONTEXT } from '../../../../../../../../constants';
import { extendFormData, setPageContext } from '../../../../../../../../store/reducers/formStore';
import { useFormOnBlur } from '../../../../../../../form/hooks/formHooks';
import { useLoggedIn } from '../../../../../../../../store/reducers/user';
import { AuthInlineForm } from '../../../../../../../AuthInlineForm';
import { PARAMS_STEP_AUTH_VALIDATION_FIELDS } from './step1.config';
import { schema } from './schema';
import { onComplete } from './onComplete';
import { StepFields } from './StepFields';
import styles from './step1.module.scss';
import { useAnalytics, EventType } from 'components/hooks/analytics';

type Props = {
    onNext: () => void;
    onAutoValidationError: (e: ValidationError) => void;
};

/** Параметры кредита и авторизация */
export const Step1: React.FC<Props> = (props) => {
    const { onNext, onAutoValidationError } = props;
    const { pushEvent } = useAnalytics();
    const dispatch = useDispatch();
    const formData = useFormData();
    const { autoRun } = useFormStore();
    const isLoggedIn = useLoggedIn();
    const { topFormAdditionalData, taskManager } = useContext(CardsContext);

    const form = useForm({
        resolver: yupResolver(schema(true)),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const {
        formState: { dirtyFields: _ },
        reset,
        getValues,
        handleSubmit,
    } = form;

    useEffect(() => {
        reset(formData, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    useEffect(() => {
        dispatch(setPageContext(PAGE_CONTEXT.STEP_1));
        if (autoRun) {
            schema(isLoggedIn).validate(formData).then(onSubmit).catch(onAutoValidationError);
        }
    }, [autoRun]);

    const emitFirstStepAnalytics = useCallback(() => {
        const [region, salary, patronymic] = getValues([
            FORM_FIELDS.REGION,
            FORM_FIELDS.MONTHLY_SALARY,
            FORM_FIELDS.MIDDLE_NAME,
        ]);
        const purpose = CARD_PURPOSES.find((item) => item.value === topFormAdditionalData.purpose);
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Шаг 1|Параметры',
            eventLabel: [
                `Доход - ${salary}`,
                `Город получения - ${region}`,
                `Цель кредитной карты - ${purpose ? purpose.label : ''}`,
                `Отчество - ${patronymic === null ? 'нет' : 'да'}`,
            ].join('|'),
        });
    }, [pushEvent, getValues, topFormAdditionalData.purpose]);

    const stepOnNext = useCallback(() => {
        taskManager.register(onComplete());
        emitFirstStepAnalytics();
        onNext();
    }, [emitFirstStepAnalytics, onNext, taskManager]);

    const onSubmit: SubmitHandler<FE.FormData> = useCallback(
        async (validValues) => {
            dispatch(extendFormData({ ...validValues, [FORM_FIELDS.AGREEMENT]: true }));
            stepOnNext();
        },
        [dispatch, stepOnNext],
    );

    const formOnBlur = useFormOnBlur(form, false);

    const authFormDefaultValues = useMemo(() => pick(formData, PARAMS_STEP_AUTH_VALIDATION_FIELDS), [formData]);

    const onLogin = useCallback(
        (formData: FieldValues) => {
            dispatch(extendFormData(formData));
            window.location.reload();

            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Вход в лк',
                eventLabel: 'Войти Сравни',
                eventValue: undefined,
            });
        },
        [dispatch, pushEvent],
    );

    const onLoginFailed = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: 'Подбор кредита',
            eventAction: 'Ошибка авторизации',
            eventLabel: 'Сравни',
        });
    }, [pushEvent]);

    const onGetOTPButtonClick = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Получить код по СМС',
        });
    }, [pushEvent]);

    return (
        <>
            {isLoggedIn ? (
                <FormProvider {...form}>
                    <form onBlur={formOnBlur} onSubmit={handleSubmit(onSubmit)}>
                        <StepFields />
                    </form>
                </FormProvider>
            ) : (
                <AuthInlineForm
                    additionalValidationSchema={schema(false)}
                    getOTPButtonProps={{
                        additionalValidationFields: PARAMS_STEP_AUTH_VALIDATION_FIELDS,
                        onClick: onGetOTPButtonClick,
                    }}
                    onLogin={onLogin}
                    onLoginFailed={onLoginFailed}
                    defaultValues={authFormDefaultValues}
                    className={styles.cardsAuthForm}>
                    <StepFields />
                </AuthInlineForm>
            )}
        </>
    );
};
